import type { ReactElement } from "react";

export type DemoStatus = "live" | "coming-soon";

export interface DemoEntry {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly category: string;
  readonly level: string;
  readonly duration: string;
  readonly status: DemoStatus;
  readonly route: string;
  readonly tags: readonly string[];
  readonly standards?: readonly string[];
  readonly thumbnail: ReactElement;
}

function MoonPhasesThumbnail() {
  return (
    <svg aria-hidden="true" viewBox="0 0 320 180">
      <rect fill="#05070d" height="180" width="320" />
      <circle cx="52" cy="36" fill="#f6f9ff" opacity="0.5" r="1.4" />
      <circle cx="120" cy="24" fill="#f6f9ff" opacity="0.7" r="1" />
      <circle cx="220" cy="40" fill="#f6f9ff" opacity="0.5" r="1.2" />
      <circle cx="286" cy="26" fill="#f6f9ff" opacity="0.6" r="1.1" />
      <circle cx="262" cy="140" fill="#f6f9ff" opacity="0.5" r="1.3" />
      <circle cx="40" cy="150" fill="#f6f9ff" opacity="0.6" r="1" />
      <ellipse
        cx="160"
        cy="94"
        fill="none"
        rx="118"
        ry="52"
        stroke="rgba(156, 195, 240, 0.4)"
        strokeDasharray="5 7"
      />
      <circle cx="160" cy="94" fill="#3f6ea8" r="22" />
      <path d="M160 72a22 22 0 0 1 0 44z" fill="#16273c" />
      <circle cx="278" cy="94" fill="#cfd4dc" r="11" />
      <path d="M278 83a11 11 0 0 1 0 22z" fill="#2a2f38" />
      <circle cx="30" cy="94" fill="#ffb347" r="14" />
      <circle cx="30" cy="94" fill="#fff3d6" r="8" />
    </svg>
  );
}

function SeasonsThumbnail() {
  return (
    <svg aria-hidden="true" viewBox="0 0 320 180">
      <rect fill="#05070d" height="180" width="320" />
      <circle cx="160" cy="90" fill="#ffb347" r="20" />
      <circle cx="160" cy="90" fill="#fff3d6" r="12" />
      <ellipse
        cx="160"
        cy="90"
        fill="none"
        rx="132"
        ry="58"
        stroke="rgba(156, 195, 240, 0.4)"
        strokeDasharray="5 7"
      />
      {/* Tilted Earth at two solstices */}
      <g transform="translate(292 90)">
        <circle fill="#3f6ea8" r="15" />
        <path d="M0 -15a15 15 0 0 0 0 30z" fill="#16273c" />
        <line
          stroke="#e9eef7"
          strokeWidth="2"
          transform="rotate(23.4)"
          x1="0"
          x2="0"
          y1="-21"
          y2="21"
        />
      </g>
      <g transform="translate(28 90)">
        <circle fill="#3f6ea8" r="15" />
        <path d="M0 -15a15 15 0 0 1 0 30z" fill="#16273c" />
        <line
          stroke="#e9eef7"
          strokeWidth="2"
          transform="rotate(23.4)"
          x1="0"
          x2="0"
          y1="-21"
          y2="21"
        />
      </g>
      <text
        fill="#94a3bb"
        fontFamily="Space Grotesk, sans-serif"
        fontSize="11"
        textAnchor="middle"
        x="292"
        y="130"
      >
        JUN
      </text>
      <text
        fill="#94a3bb"
        fontFamily="Space Grotesk, sans-serif"
        fontSize="11"
        textAnchor="middle"
        x="28"
        y="130"
      >
        DEC
      </text>
    </svg>
  );
}

function HorizonThumbnail() {
  return (
    <svg aria-hidden="true" viewBox="0 0 320 180">
      <rect fill="#05070d" height="180" width="320" />
      <circle cx="70" cy="34" fill="#f6f9ff" opacity="0.6" r="1.2" />
      <circle cx="150" cy="22" fill="#f6f9ff" opacity="0.7" r="1" />
      <circle cx="238" cy="30" fill="#f6f9ff" opacity="0.5" r="1.3" />
      <circle cx="286" cy="52" fill="#f6f9ff" opacity="0.6" r="1.1" />
      {/* Horizon line */}
      <line
        stroke="rgba(156, 195, 240, 0.55)"
        strokeWidth="2"
        x1="20"
        x2="300"
        y1="132"
        y2="132"
      />
      {/* Sky dome / altitude arc */}
      <path
        d="M40 132a120 120 0 0 1 240 0"
        fill="none"
        stroke="rgba(156, 195, 240, 0.35)"
        strokeDasharray="5 7"
      />
      {/* Altitude ray to an object above the horizon */}
      <line
        stroke="#f5b84b"
        strokeWidth="2"
        x1="160"
        x2="236"
        y1="132"
        y2="70"
      />
      <circle cx="236" cy="70" fill="#ffb347" r="9" />
      <circle cx="236" cy="70" fill="#fff3d6" r="5" />
      {/* Observer */}
      <circle cx="160" cy="132" fill="#77c7ba" r="4" />
      <text
        fill="#94a3bb"
        fontFamily="Space Grotesk, sans-serif"
        fontSize="11"
        textAnchor="middle"
        x="196"
        y="120"
      >
        altitude
      </text>
    </svg>
  );
}

export const CATEGORIES = [
  "Astrophysics",
  "Physics",
  "Biology",
  "Chemistry",
  "Mathematics",
] as const;

export const DEMOS: readonly DemoEntry[] = [
  {
    id: "moon-phases",
    title: "Phases of the Moon",
    description:
      "Why the Moon changes shape: sunlight, orbital position, and the view from Earth in one live 3D setup.",
    category: "Astrophysics",
    level: "Grades 4–10",
    duration: "10–20 min",
    status: "live",
    route: "#/demos/moon-phases",
    tags: [
      "moon",
      "lunar",
      "orbit",
      "eclipse",
      "tidal locking",
      "astronomy",
      "visual learning",
      "waxing",
      "waning",
      "gibbous",
      "crescent",
    ],
    standards: ["NGSS MS-ESS1-1", "NGSS MS-ESS1-2"],
    thumbnail: <MoonPhasesThumbnail />,
  },
  {
    id: "earth-seasons",
    title: "Earth's Seasons & Axial Tilt",
    description:
      "How the 23.4° tilt creates opposite seasons in the North and South — and months of day or night at the Poles.",
    category: "Astrophysics",
    level: "Grades 4–10",
    duration: "10–15 min",
    status: "live",
    route: "#/demos/earth-seasons",
    tags: [
      "seasons",
      "tilt",
      "solstice",
      "equinox",
      "poles",
      "astronomy",
      "insolation",
      "axial tilt",
      "day length",
    ],
    standards: ["NGSS MS-ESS1-1", "NGSS 5-ESS1-2"],
    thumbnail: <SeasonsThumbnail />,
  },
  {
    id: "local-horizon",
    title: "Your Horizon and Altitude",
    description:
      "Stand somewhere on Earth and watch the Sun rise, cross your sky, and set — day length and noon height still come from latitude.",
    category: "Astrophysics",
    level: "Grades 4–10",
    duration: "10–15 min",
    status: "live",
    route: "#/demos/local-horizon",
    tags: [
      "horizon",
      "altitude",
      "latitude",
      "sky",
      "observer",
      "astronomy",
      "azimuth",
      "zenith",
      "sun path",
    ],
    standards: ["NGSS MS-ESS1-1", "NGSS 5-ESS1-2"],
    thumbnail: <HorizonThumbnail />,
  },
];

export function searchDemos(
  demos: readonly DemoEntry[],
  query: string,
  category: string | null,
): readonly DemoEntry[] {
  const needle = query.trim().toLowerCase();

  return demos.filter((demo) => {
    if (category !== null && demo.category !== category) {
      return false;
    }
    if (needle === "") {
      return true;
    }
    const haystack = [
      demo.title,
      demo.description,
      demo.category,
      ...demo.tags,
      ...(demo.standards ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return needle.split(/\s+/).every((word) => haystack.includes(word));
  });
}
